import React, { useState } from 'react'
import { useDarkMode } from '../../context/DarkModeContext'
import EmailSettingsModal from './EmailSettingsModal'
import { emailSettingsStyles } from './emailSettingsStyles'

const SIGNATURE_FIELDS = ['name', 'position', 'companyName', 'phone', 'ext', 'email', 'mc', 'usdot', 'website']

// Mirrors the layout the server renders, so the preview matches what is sent.
export const SignaturePreview = ({ data, mutedClass }) => {
	const contact = [data.phone && (data.ext ? `${data.phone} ext ${data.ext}` : data.phone), data.email].filter(Boolean)
	const authority = [data.mc && `MC ${data.mc}`, data.usdot && `USDOT ${data.usdot}`].filter(Boolean)
	return (
		<div className="text-sm leading-relaxed break-words">
			{data.name && <div className="font-semibold">{data.name}</div>}
			{data.position && <div className={mutedClass}>{data.position}</div>}
			{data.companyName && <div className="font-medium">{data.companyName}</div>}
			{contact.length > 0 && <div className={mutedClass}>{contact.join(' | ')}</div>}
			{authority.length > 0 && <div className={mutedClass}>{authority.join(' | ')}</div>}
			{data.website && <div className={mutedClass}>{data.website}</div>}
		</div>
	)
}

const SignatureModal = ({ account, onSave, onDelete, onClose }) => {
	const { isDarkMode } = useDarkMode()
	const isLight = !isDarkMode
	const styles = emailSettingsStyles(isLight)
	const savedData = account.signature?.signatureData || {}
	const [data, setData] = useState(savedData)
	const hasChanges = SIGNATURE_FIELDS.some(key => (data[key] || '') !== (savedData[key] || ''))
	const requestClose = () => {
		if (!hasChanges || window.confirm('Discard your changes?')) onClose()
	}
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [error, setError] = useState(null)

	const updateField = (key, value) => {
		setData(previous => ({ ...previous, [key]: value }))
		setError(null)
	}

	const handleSave = async () => {
		if (!data.name?.trim()) {
			setError('Add a name for the signature.')
			return
		}
		setSaving(true)
		try {
			await onSave(account._id, data)
			onClose()
		} catch (err) {
			setError(err.message)
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async () => {
		setDeleting(true)
		setError(null)
		try {
			if (await onDelete(account)) onClose()
		} catch (err) {
			setError(err.message)
		} finally {
			setDeleting(false)
		}
	}

	const field = (key, label, placeholder, type = 'text') => (
		<div>
			<label className={styles.label} htmlFor={`signature-${key}`}>{label}</label>
			<input
				id={`signature-${key}`}
				type={type}
				value={data[key] || ''}
				placeholder={placeholder}
				onChange={event => updateField(key, event.target.value)}
				className={styles.input}
			/>
		</div>
	)

	const hasContent = SIGNATURE_FIELDS.some(key => data[key]?.trim())

	return (
		<EmailSettingsModal
			title="Email signature"
			subtitle={account.email}
			wide
			onClose={requestClose}
			footer={
				<>
					{onDelete && (
						<button
							type="button"
							onClick={handleDelete}
							disabled={deleting || saving}
							className={`w-full sm:w-auto sm:mr-auto px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50 ${
								isLight ? 'text-red-600 hover:bg-red-50' : 'text-red-400 hover:bg-red-500/10'
							}`}
						>
							{deleting ? 'Deleting…' : 'Delete signature'}
						</button>
					)}
					<button type="button" onClick={requestClose} className={`${styles.secondaryButton} w-full sm:w-auto py-2`}>
						Cancel
					</button>
					<button type="button" onClick={handleSave} disabled={saving} className={`${styles.primaryButton} w-full sm:w-auto`}>
						{saving ? 'Saving…' : 'Save signature'}
					</button>
				</>
			}
		>
			{error && (
				<div role="alert" className={`${styles.errorBox} mb-4`}>
					{error}
				</div>
			)}
			<div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_16rem]">
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2.5 content-start">
					{field('name', 'Name', 'Jane Doe')}
					{field('position', 'Position', 'Dispatcher')}
					<div className="sm:col-span-2">{field('companyName', 'Company', 'Your Trucking LLC')}</div>
					<div className="grid grid-cols-[minmax(0,1fr)_4.5rem] gap-2">
						{field('phone', 'Phone', '(555) 123-4567')}
						{field('ext', 'Ext', '12')}
					</div>
					{field('email', 'Email', 'you@company.com', 'email')}
					{field('mc', 'MC number', '123456')}
					{field('usdot', 'USDOT number', '1234567')}
					<div className="sm:col-span-2">{field('website', 'Website', 'yourcompany.com')}</div>
				</div>
				<div className="order-first md:order-last">
					<p className={styles.sectionTitle}>Preview</p>
					<div className={`md:sticky md:top-0 rounded-lg bg-white text-gray-900 p-4 ring-1 ${isLight ? 'ring-gray-200' : 'ring-white/10'}`}>
						<div className="border-t border-gray-200 pt-3">
							{hasContent ? (
								<SignaturePreview data={data} mutedClass="text-gray-600" />
							) : (
								<p className="text-sm text-gray-400">Your signature shows here as you type.</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</EmailSettingsModal>
	)
}

export default SignatureModal
